/**
 * 3 Nov 2026 (D-1103-PAIR): "Usually with Maria C. (5 visits). Maria C.'s next free times: ..." at the top of the volunteer
 * step of Book. One button per free time fills the day and the part and picks that volunteer to ASK; it assigns nobody
 * (the volunteer's own Confirm does) and sends nothing. No availability marked: one press queues the availability link
 * (the Outbox, the cancel window). The tenant is never named to a volunteer by any of this.
 */
import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CategoryKey } from '@shared/volunteers';
import { FREE_MAX_BUTTONS, buttonMinutes, buttonWhen, freeSentence, type FreeTime, type UsualPartnerEntry, type UsualPartnerView } from '@shared/usual-partner-1103';
import { apiFetch } from '@/lib/api';
import { useAnnounceSafe } from '@/components/Announcer';
import { Button } from '@/components/ui/button';
import { errorText } from '@/features/people/api';
import { sendPress } from './api';

export function useUsualPartner(q: { tenantId: string | null; category: CategoryKey | null; dayKey: string; minutes: number }) {
  const ok = !!q.tenantId && !!q.category;
  return useQuery({
    queryKey: ['usual-partner', q.tenantId, q.category, q.dayKey, q.minutes],
    queryFn: () => apiFetch<UsualPartnerView>(`/api/volunteers/usual-partner?tenantId=${encodeURIComponent(q.tenantId!)}&category=${q.category!}${/^\d{8}$/.test(q.dayKey) ? `&dayKey=${q.dayKey}&minutes=${q.minutes}` : ''}`),
    enabled: ok,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

export function UsualPartnerCard({ view, onBook }: { view: UsualPartnerView | undefined; onBook: (volunteerId: string, dayKey: string, minutes: number | null) => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const announce = useAnnounceSafe();
  const said = useRef('');
  const [note, setNote] = useState<string | null>(null);
  const [sending, setSending] = useState<string | null>(null);
  const partners = view?.enabled ? view.partners : [];
  const names = partners.map((p) => `${p.name} (${t('pair1103.visits', { count: p.visits })})`).join(t('pair1103.and'));
  const shown = partners.reduce((n, p) => n + Math.min(p.times.length, FREE_MAX_BUTTONS), 0);
  // read out once per set of names (never on a background refresh)
  useEffect(() => {
    const key = partners.map((p) => p.volunteerId).join('|');
    if (!key || said.current === key) return;
    said.current = key;
    announce(t('pair1103.announce', { names, count: shown }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partners.map((p) => p.volunteerId).join('|')]);
  if (!partners.length) return null;

  const book = (p: UsualPartnerEntry, f: FreeTime) => {
    onBook(p.volunteerId, f.dayKey, buttonMinutes(f));
    const m = t('pair1103.picked', { when: buttonWhen(f, lang), name: p.firstName });
    setNote(m);
    announce(m);
  };
  const sendLink = async (p: UsualPartnerEntry) => {
    setSending(p.volunteerId);
    try {
      const r = await sendPress(p.volunteerId, 'send-availability');
      setNote(r.message);
      announce(r.message);
    } catch (e) {
      const m = errorText(e);
      setNote(m);
      announce(m, 'assertive');
    } finally {
      setSending(null);
    }
  };

  return (
    <section className="field" aria-labelledby="pair-h" data-testid="usual-partner">
      <p id="pair-h" className="eyebrow">
        <b>{t('pair1103.usually', { names })}</b>
      </p>
      {partners.map((p) => (
        <div key={p.volunteerId} data-testid={`usual-${p.volunteerId}`}>
          {!p.hasAvailability ? (
            <>
              <p className="help">{t('pair1103.noAvail', { name: p.firstName })}</p>
              <Button variant="secondary" size="sm" disabled={sending === p.volunteerId} onClick={() => void sendLink(p)} data-testid={`usual-send-${p.volunteerId}`}>
                {t('pair1103.sendLink', { name: p.firstName })}
              </Button>
              <p className="help">{t('pair1103.sendLinkHelp')}</p>
            </>
          ) : p.times.length ? (
            <>
              <p data-testid={`usual-sentence-${p.volunteerId}`}>{freeSentence(p.name, p.times, lang)}</p>
              <div className="chips dense" role="group" aria-label={t('pair1103.region')}>
                {p.times.slice(0, FREE_MAX_BUTTONS).map((f) => (
                  <button key={`${f.kind}-${f.dayKey}`} type="button" className="chip" onClick={() => book(p, f)} data-testid={`usual-book-${p.volunteerId}-${f.dayKey}`}>
                    {t('pair1103.bookBtn', { when: buttonWhen(f, lang), name: p.firstName })}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="help">{t('pair1103.none', { name: p.firstName })}</p>
          )}
        </div>
      ))}
      {note ? (
        <p className="help" data-testid="usual-note">
          {note}
        </p>
      ) : null}
    </section>
  );
}
